import { Link, useParams, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { useProducts, useCategories, CATEGORY_NAMES } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Stagger, Item } from "../components/Motion";
import { Skeleton } from "../components/ui/skeleton";
import { Seo, breadcrumbLd } from "../components/Seo";

const ComingSoon = () => (
  <div data-testid="coming-soon-card" className="flex aspect-square flex-col justify-end rounded-2xl border border-dashed border-jute bg-card/50 p-8">
    <p className="eyebrow">Coming soon</p>
    <p className="t-h3 mt-3">More pieces for the same system.</p>
    <p className="t-small mt-3">More modules that fit the Pustak Rack's bays, built the same way: flat-packed, DIY, made for Hyderabad homes.</p>
  </div>
);

const TITLES = {
  storage: "Modular Storage & DIY Bookshelves in Hyderabad | H&W Furniture",
  modules: "Pustak Rack Add-on Modules: Drawer, Lift-top, Doors | H&W Furniture",
};

export default function Shop() {
  const { category } = useParams();
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const { data: cats = [] } = useCategories();
  const params = {};
  if (category) params.category = category;
  if (q) params.q = q;
  const { data = [], isLoading } = useProducts(params);
  const blurb = cats.find((c) => c.slug === category)?.blurb;

  return (
    <div className="container-hw py-10 lg:py-16">
      <Seo
        title={TITLES[category] || "Shop DIY Modular Furniture Online in Hyderabad | H&W Furniture"}
        description="Shop flat-pack, ready-to-assemble modular furniture online. The Pustak Rack DIY bookshelf plus drawer, lift-top and door modules you build at home. 24 Hrs free delivery across Hyderabad."
        path={category ? `/shop/${category}` : "/shop"}
        noindex={!!q}
        jsonLd={[breadcrumbLd([["Home", "/"], ["Shop", "/shop"], ...(category ? [[CATEGORY_NAMES[category], `/shop/${category}`]] : [])])]}
      />
      <div className="rise">
        <p className="eyebrow">Shop</p>
        <h1 data-testid="shop-title" className="t-h1 mt-3">{category ? CATEGORY_NAMES[category] : "DIY modular furniture"}</h1>
        <p className="t-lead mt-4 max-w-xl">{blurb || "Flat-packed, built at home by you, and delivered free across Hyderabad within 24 hours."}</p>
      </div>
      {cats.length > 1 && (
        <nav data-testid="shop-category-nav" className="rise d1 mt-8 flex flex-wrap gap-2">
          {[{ slug: "", name: "Everything" }, ...cats].map((c) => (
            <Link key={c.slug} to={c.slug ? `/shop/${c.slug}` : "/shop"} data-testid={`shop-cat-${c.slug || "all"}`} aria-current={(category || "") === c.slug ? "page" : undefined} className={`chip px-4 py-2 text-sm ${(category || "") === c.slug ? "chip-active" : ""}`}>{c.name}{c.count ? <span className="opacity-60">{c.count}</span> : null}</Link>
          ))}
        </nav>
      )}
      <div className="mt-10">
        {q && (
          <p data-testid="shop-result-count" className="mb-8 text-sm text-muted-foreground">
            {isLoading ? "Searching" : `${data.length} ${data.length === 1 ? "piece" : "pieces"}`}
            <button data-testid="clear-search-button" onClick={() => setSp({})} className="chip ml-3">for "{q}" <X className="h-3 w-3" /></button>
          </p>
        )}
        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="aspect-square rounded-2xl" />)}</div>
        ) : data.length === 0 ? (
          <div data-testid="shop-empty" className="rounded-2xl border border-dashed border-jute p-12 text-center">
            <p className="t-h3">Nothing matches that yet.</p>
            <button onClick={() => setSp({})} data-testid="reset-filters-button" className="btn-outline mt-6">See everything</button>
          </div>
        ) : (
          <Stagger data-testid="product-grid" className="grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-3 lg:gap-y-12">
            {data.map((p) => <Item key={p.slug}><ProductCard product={p} /></Item>)}
            <Item className="hidden sm:block"><ComingSoon /></Item>
          </Stagger>
        )}
      </div>
    </div>
  );
}
